import { BillItem } from '../pages/hospital/Billing/types';
import { TYPE_BILL_ITEM } from '../constants/types';

/**
 * Calculates amount for a single bill item (quantity × unitCost)
 * @param item - Bill item
 * @param usePrintPrice - Use amountAlter if available
 * @returns Calculated amount
 */
export function calculateItemAmount(item: BillItem, usePrintPrice = false): number {
  const baseAmount = usePrintPrice && item.amountAlter ? item.amountAlter : item.amount;
  const amount = Number(baseAmount) * Number(item.quantity);
  return Math.round(amount * 100) / 100; // Round to 2 decimals
}

/**
 * Calculates discount amount based on type (percentage or fixed)
 * @param subtotal - Subtotal before discount
 * @param discountValue - Discount value (percentage or fixed amount)
 * @param isPercentage - True if percentage discount
 * @returns Discount amount
 */
export function calculateDiscountAmount(
  subtotal: number,
  discountValue: number,
  isPercentage: boolean
): number {
  if (isPercentage) {
    return Math.round((subtotal * discountValue) / 100 * 100) / 100;
  }
  return discountValue;
}

/**
 * Subtotals by item type
 */
export interface ItemTypeSubtotals {
  charges: number;
  services: number;
  packages: number;
  hiddenValue: number;
  discountAmount: number;
  discountPercent: number;
  discountAdditional: number;
}

/**
 * Separates bill items by type and calculates subtotals
 * @param items - Array of bill items
 * @param officialPrices - Use official prices instead of altered
 * @returns Object with subtotals by type
 */
export function calculateItemsByType(items: BillItem[], officialPrices = false): ItemTypeSubtotals {
  const subtotals: ItemTypeSubtotals = {
    charges: 0,           // TYPE_BILL_ITEM.CHARGE (0)
    discountAmount: 0,    // TYPE_BILL_ITEM.DISCOUNT_AMOUNT (1)
    discountPercent: 0,   // TYPE_BILL_ITEM.DISCOUNT_PERCENT (2)
    services: 0,          // TYPE_BILL_ITEM.SERVICE (3)
    discountAdditional: 0, // TYPE_BILL_ITEM.DISCOUNT_ADDITIONAL_INFO (4)
    hiddenValue: 0,       // TYPE_BILL_ITEM.HIDDEN_VALUE (5)
    packages: 0,          // TYPE_BILL_ITEM.PACKAGE (6)
  };
  
  items.forEach(item => {
    const amount = officialPrices ? item.amount : (item.amountAlter || item.amount);
    const total = Number(amount) * Number(item.quantity);
    const type = item.type ? parseInt(String(item.type)) : 0;
    
    switch(type) {
      case TYPE_BILL_ITEM.CHARGE:
        subtotals.charges += total;
        break;
      case TYPE_BILL_ITEM.DISCOUNT_AMOUNT:
        subtotals.discountAmount += total;
        break;
      case TYPE_BILL_ITEM.DISCOUNT_PERCENT:
        subtotals.discountPercent += total;
        break;
      case TYPE_BILL_ITEM.SERVICE:
        subtotals.services += total;
        break;
      case TYPE_BILL_ITEM.DISCOUNT_ADDITIONAL_INFO:
        subtotals.discountAdditional += total;
        break;
      case TYPE_BILL_ITEM.HIDDEN_VALUE:
        subtotals.hiddenValue += total;
        break;
      case TYPE_BILL_ITEM.PACKAGE:
        subtotals.packages += total;
        break;
    }
  });
  
  return subtotals;
}

/**
 * Gets next order number for bill items
 * @param items - Current bill items
 * @returns Next order number
 */
export function getNextOrderNumber(items: BillItem[]): number {
  if (items.length === 0) return 1;
  const maxOrder = Math.max(...items.map(item => item.order || 0));
  return maxOrder + 1;
}

